/**
 * Billing periods helper.
 * Mantém em sincronia com /app/backend/mesafa/plans_data.py
 *
 * Usar como fallback/UI labels enquanto se faz fetch ao endpoint
 * /api/public/billing-periods.
 */

export const DEFAULT_BILLING_PERIOD = "mensal";

export const BILLING_PERIODS = [
    { id: "mensal", label: "Mensal", short_label: "Mensal", months: 1, discount_pct: 0, tag: null },
    { id: "trimestral", label: "Trimestral (3 meses)", short_label: "Trimestral", months: 3, discount_pct: 10, tag: "Poupa 10%" },
    { id: "semestral", label: "Semestral (6 meses)", short_label: "Semestral", months: 6, discount_pct: 15, tag: "Poupa 15%" },
    { id: "anual", label: "Anual (12 meses)", short_label: "Anual", months: 12, discount_pct: 25, tag: "Poupa 25%" },
];

export function getPeriod(periodId) {
    return BILLING_PERIODS.find((p) => p.id === periodId) || BILLING_PERIODS[0];
}

/**
 * Lê o pricing pré-calculado do backend (campo plan.pricing_by_period[periodId]).
 * Faz fallback para cálculo client-side se o backend não enviar dados.
 */
export function getPlanPricing(plan, periodId) {
    const period = getPeriod(periodId);
    if (!plan) return null;
    if (plan.pricing_by_period && plan.pricing_by_period[period.id]) {
        return plan.pricing_by_period[period.id];
    }
    // fallback (planos custom não terão pricing_by_period)
    const baseAoa = Number(plan.price_aoa || 0);
    const baseUsd = Number(plan.price_usd || 0);
    const months = period.months;
    const discount = period.discount_pct / 100;
    const totalAoa = Math.round(baseAoa * months * (1 - discount) * 100) / 100;
    const totalUsd = Math.round(baseUsd * months * (1 - discount) * 100) / 100;
    return {
        period_id: period.id,
        period_label: period.label,
        months,
        discount_pct: period.discount_pct,
        amount_aoa: totalAoa,
        amount_usd: totalUsd,
        monthly_aoa: months ? Math.round((totalAoa / months) * 100) / 100 : baseAoa,
        monthly_usd: months ? Math.round((totalUsd / months) * 100) / 100 : baseUsd,
        base_aoa: baseAoa,
        base_usd: baseUsd,
    };
}

/**
 * Devolve a string formatada do total a pagar para um plano + período.
 * Não usar para planos custom (agencia).
 */
export function formatTotal(pricing, currency) {
    if (!pricing) return "";
    if (currency === "USD") {
        return `${Number(pricing.amount_usd).toLocaleString("en-US", { maximumFractionDigits: 0 })} USD`;
    }
    return `${Number(pricing.amount_aoa).toLocaleString("pt-PT", { maximumFractionDigits: 0 })} AOA`;
}

/**
 * Devolve a string do preço efectivo por mês (após desconto).
 */
export function formatMonthly(pricing, currency) {
    if (!pricing) return "";
    if (currency === "USD") {
        return `${Number(pricing.monthly_usd).toLocaleString("en-US", { maximumFractionDigits: 0 })} USD`;
    }
    return `${Number(pricing.monthly_aoa).toLocaleString("pt-PT", { maximumFractionDigits: 0 })} AOA`;
}

/**
 * Devolve o valor poupado em relação ao preço base (sem desconto) para o mesmo período.
 */
export function savingsAmount(pricing, currency) {
    if (!pricing || !pricing.discount_pct) return null;
    const months = pricing.months || 1;
    if (currency === "USD") {
        const baseTotal = pricing.base_usd * months;
        return Math.round(baseTotal - pricing.amount_usd);
    }
    const baseTotal = pricing.base_aoa * months;
    return Math.round(baseTotal - pricing.amount_aoa);
}

export function formatSavings(pricing, currency) {
    const v = savingsAmount(pricing, currency);
    if (!v) return null;
    if (currency === "USD") return `${Number(v).toLocaleString("en-US")} USD`;
    return `${Number(v).toLocaleString("pt-PT")} AOA`;
}
